import { useCallback, useEffect, useState } from "react";
import { ChevronRight, CloudUpload, Play } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { Panel, SectionLabel, StatusBadge } from "@/components/common/ui-kit";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const STATE = {
  OK: ["green", "Regolare"], AVVISI: ["amber", "Completato con avvisi"], FALLITO: ["red", "Fallito"], SCADUTO: ["red", "Scaduto"],
  IN_CORSO: ["blue", "In corso"], MAI_ESEGUITO: ["amber", "Mai eseguito"], DISABILITATO: ["neutral", "Non configurato"],
  SOLO_PRODUZIONE: ["blue", "Attivo solo in produzione"],
};
const RUN = { COMPLETATO: "green", COMPLETATO_CON_AVVISI: "amber", FALLITO: "red", IN_CORSO: "blue" };
const label = (s) => (s || "").replaceAll("_", " ").toLowerCase();

const Fact = ({ title, value, sub, testId }) => (
  <div className="rounded-lg border border-slate-100 p-3" data-testid={testId}>
    <p className="text-xs text-slate-500">{title}</p>
    <p className="mt-1 text-sm font-semibold text-slate-800">{value}</p>
    {sub && <p className="mt-0.5 text-xs text-slate-500">{sub}</p>}
  </div>
);

const RunNow = ({ disabled, onDone }) => {
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try { await api.post("/admin/m365-backup/run"); toast.success("Backup avviato. Lo stato si aggiorna a fine esecuzione."); onDone(); }
    catch (e) { toast.error(errorMessage(e)); } finally { setBusy(false); }
  };
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button size="sm" disabled={disabled || busy} data-testid="m365-backup-run-btn"><Play className="mr-1.5 h-4 w-4" />Avvia backup ora</Button>
      </AlertDialogTrigger>
      <AlertDialogContent data-testid="m365-backup-run-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>Avviare il backup Microsoft 365?</AlertDialogTitle>
          <AlertDialogDescription>Verranno copiati su SharePoint i dati e i documenti nuovi o modificati. I dati di ON! non vengono modificati. L'operazione viene registrata nell'audit.</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="m365-backup-run-cancel">Annulla</AlertDialogCancel>
          <AlertDialogAction onClick={go} data-testid="m365-backup-run-confirm">Avvia</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

const what = (e) => (e.type === "file" ? `Documento ${e.document || "—"} · versione ${e.version}`
  : e.type === "bucket" ? `Registro ${e.coll} del ${e.day}` : `Record ${e.coll} · ${e.on_id}`);

const RunDetail = ({ r }) => {
  const errs = r.errors || [], warns = r.warnings || [];
  if (!errs.length && !warns.length) {
    return <p className="w-full pb-1 text-xs text-slate-500" data-testid={`m365-backup-run-${r.id}-detail`}>Nessun elemento escluso: tutto copiato.</p>;
  }
  return (
    <div className="w-full space-y-1 rounded-lg bg-amber-50/60 p-3 text-xs" data-testid={`m365-backup-run-${r.id}-detail`}>
      {errs.map((e, i) => <p key={`e${i}`} className="text-slate-700" data-testid={`m365-backup-run-${r.id}-error-${i}`}><span className="font-semibold">Non copiato · {what(e)}</span> — {e.error}</p>)}
      {warns.map((w, i) => <p key={`w${i}`} className="text-amber-800" data-testid={`m365-backup-run-${r.id}-warning-${i}`}>Avviso — {w}</p>)}
      {errs.length > 0 && <p className="pt-1 text-slate-500">Gli elementi non copiati vengono ritentati automaticamente al prossimo backup.</p>}
    </div>
  );
};

const RunRow = ({ r }) => {
  const [open, setOpen] = useState(false);
  const n = (r.errors?.length || 0) + (r.warnings?.length || 0);
  return (
    <li className="py-2.5 text-sm" data-testid={`m365-backup-run-${r.id}`}>
      <button type="button" onClick={() => setOpen(!open)} className="flex w-full flex-wrap items-center gap-x-3 gap-y-1 text-left hover:opacity-80" data-testid={`m365-backup-run-${r.id}-toggle`}>
        <ChevronRight className={`h-4 w-4 text-slate-400 transition-transform ${open ? "rotate-90" : ""}`} />
        <StatusBadge tone={RUN[r.status] || "neutral"}>{label(r.status)}</StatusBadge>
        <span className="text-slate-700">{fmtDateTime(r.started_at)}</span>
        <span className="text-xs text-slate-500">{r.trigger === "manual" ? "manuale" : r.trigger}{r.duration_s != null && ` · ${r.duration_s}s`}
          {r.summary && ` · ${r.summary.nuovi_record + r.summary.record_modificati} record, ${r.summary.file_nuovi + r.summary.file_modificati} file, ${r.summary.errori} errori`}</span>
        {n > 0 && <span className="text-xs font-medium text-amber-700">Vedi dettaglio</span>}
      </button>
      {r.blocking?.length > 0 && <p className="mt-1 w-full text-xs text-red-600">{r.blocking.join(" · ")}</p>}
      {open && <div className="mt-2 pl-7"><RunDetail r={r} /></div>}
    </li>
  );
};

const RunsList = ({ runs }) => (
  <ul className="divide-y divide-slate-100" data-testid="m365-backup-runs">
    {runs.map((r) => <RunRow key={r.id} r={r} />)}
    {!runs.length && <li className="py-2 text-sm text-slate-500">Nessuna esecuzione registrata.</li>}
  </ul>
);

export const BackupM365Panel = () => {
  const { me } = useAuth();
  const [h, setH] = useState(null);
  const [runs, setRuns] = useState([]);
  const load = useCallback(() => {
    api.get("/admin/m365-backup/status").then((r) => setH(r.data)).catch((e) => toast.error(errorMessage(e)));
    api.get("/admin/m365-backup/runs").then((r) => setRuns(r.data)).catch(() => setRuns([]));
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (h?.state !== "IN_CORSO") return undefined; const t = setTimeout(load, 5000); return () => clearTimeout(t); }, [h, load]);
  if (!h) return null;
  const [tone, text] = STATE[h.state] || ["neutral", h.state];
  const ok = h.last_success, ko = h.last_failure;
  return (
    <Panel className="p-5 lg:col-span-3" data-testid="m365-backup-panel">
      <div className="mb-4 flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-brand-50 text-brand-600"><CloudUpload className="h-4 w-4" /></span>
          <div><SectionLabel>Backup aziendale</SectionLabel><h2 className="font-display text-lg font-bold text-ink">Microsoft 365 · SharePoint</h2></div>
        </div>
        <div className="flex items-center gap-3">
          <StatusBadge tone={tone} data-testid="m365-backup-state">{text}</StatusBadge>
          {me?.role?.key === "super_admin" && <RunNow disabled={h.mode === "disabled" || ["IN_CORSO", "SOLO_PRODUZIONE"].includes(h.state)} onDone={() => setTimeout(load, 1500)} />}
        </div>
      </div>
      {h.mode === "disabled" && <p className="mb-4 rounded-lg bg-amber-50 p-3 text-xs text-amber-800" data-testid="m365-backup-disabled-note">Backup non ancora configurato: servono i secrets M365_BACKUP_MODE, M365_DRIVE_ID e M365_BACKUP_ROOT.</p>}
      {h.state === "SOLO_PRODUZIONE" && <p className="mb-4 rounded-lg bg-sky-50 p-3 text-xs text-sky-800" data-testid="m365-backup-prod-only-note">Backup configurato. In questo ambiente non viene eseguito: la copia SharePoint è scritta solo dall'ambiente di produzione (02:30, recupero 05:30).</p>}
      <div className="mb-5 grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Fact title="Ultimo backup riuscito" value={ok ? fmtDateTime(ok.ended_at) : "Nessuno"} sub={ok && label(ok.status)} testId="m365-backup-last-success" />
        <Fact title="Ultimo backup fallito" value={ko ? fmtDateTime(ko.ended_at) : "Nessuno"} sub={ko?.blocking?.[0]} testId="m365-backup-last-failure" />
        <Fact title="Ore dall'ultimo successo" value={h.hours_since_success ?? "—"} sub={`Scaduto oltre ${h.stale_after_hours} ore`} testId="m365-backup-age" />
      </div>
      <SectionLabel>Ultime esecuzioni</SectionLabel>
      <RunsList runs={runs} />
    </Panel>
  );
};
